import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  $isTableSelection,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from '@lexical/table';
import { $findMatchingParent } from '@lexical/utils';
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  LexicalEditor,
} from 'lexical';

export interface TableContext {
  cell: TableCellNode;
  row: TableRowNode;
  table: TableNode;
  rowIndex: number;
  colIndex: number;
}

/**
 * Gets the table context from the current selection
 */
export function getTableContextFromSelection(): TableContext | null {
  const selection = $getSelection();

  let cell: TableCellNode | null = null;

  if ($isTableSelection(selection)) {
    const nodes = selection.getNodes();
    const firstCell = nodes.find((node) => $isTableCellNode(node));
    const firstRow = nodes.find((node) => $isTableRowNode(node));
    if (firstCell && $isTableCellNode(firstCell)) {
      cell = firstCell;
    } else if (firstRow && $isTableRowNode(firstRow)) {
      const rowFirstCell = firstRow.getFirstChild();
      cell =
        rowFirstCell && $isTableCellNode(rowFirstCell) ? rowFirstCell : null;
    }
  } else if ($isRangeSelection(selection)) {
    cell = $findMatchingParent(selection.anchor.getNode(), $isTableCellNode);
  }

  if (!cell) {
    return null;
  }

  const row = cell.getParent();
  if (!$isTableRowNode(row)) {
    return null;
  }

  const table = row.getParent();
  if (!$isTableNode(table)) {
    return null;
  }

  const colIndex = row.getChildren().indexOf(cell);
  const rowIndex = table.getChildren().indexOf(row);

  return { cell, row, table, rowIndex, colIndex };
}

/**
 * Inserts a new table at the current selection
 */
export function insertTable(
  editor: LexicalEditor,
  rows: number,
  columns: number,
  includeHeaderRow: boolean = true
): void {
  editor.update(() => {
    const table = $createTableNode();

    for (let r = 0; r < rows; r++) {
      const row = $createTableRowNode();
      table.append(row);

      for (let c = 0; c < columns; c++) {
        const headerState =
          r === 0 && includeHeaderRow
            ? TableCellHeaderStates.ROW
            : TableCellHeaderStates.NO_STATUS;
        const cell = $createTableCellNode(headerState);
        row.append(cell);
      }
    }

    const root = $getRoot();
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      try {
        const topLevel = selection.anchor.getNode().getTopLevelElementOrThrow();
        topLevel.insertAfter(table);
      } catch {
        // If we can't get a top level element, append to root
        root.append(table);
      }
    } else {
      // No selection, append to root
      root.append(table);
    }

    // Add a paragraph after the table for easier continuation typing
    table.insertAfter($createParagraphNode());
    table.selectStart();
  });
}

/**
 * Inserts a row above the current row
 */
export function insertRowAbove(editor: LexicalEditor): void {
  editor.update(() => {
    const ctx = getTableContextFromSelection();
    if (!ctx) return;

    const { row } = ctx;
    const cols = row.getChildrenSize();
    const newRow = $createTableRowNode();

    for (let i = 0; i < cols; i++) {
      newRow.append($createTableCellNode(TableCellHeaderStates.NO_STATUS));
    }

    row.insertBefore(newRow);
  });
}

/**
 * Inserts a row below the current row
 */
export function insertRowBelow(editor: LexicalEditor): void {
  editor.update(() => {
    const ctx = getTableContextFromSelection();
    if (!ctx) return;

    const { row } = ctx;
    const cols = row.getChildrenSize();
    const newRow = $createTableRowNode();

    for (let i = 0; i < cols; i++) {
      newRow.append($createTableCellNode(TableCellHeaderStates.NO_STATUS));
    }

    row.insertAfter(newRow);
  });
}

/**
 * Inserts a column to the left of the current column
 */
export function insertColumnLeft(editor: LexicalEditor): void {
  editor.update(() => {
    const ctx = getTableContextFromSelection();
    if (!ctx) return;

    const { table, colIndex } = ctx;
    const rows = table.getChildren();

    rows.forEach((r) => {
      if (!$isTableRowNode(r)) return;

      const cells = r.getChildren();
      const refCell = cells[colIndex];

      if (refCell && $isTableCellNode(refCell)) {
        // Preserve header state from the reference cell
        const headerState = refCell.getHeaderStyles();
        const newCell = $createTableCellNode(headerState);
        // Insert before the reference cell at colIndex
        refCell.insertBefore(newCell);
      } else {
        // If no reference cell, append at the end
        const newCell = $createTableCellNode(TableCellHeaderStates.NO_STATUS);
        r.append(newCell);
      }
    });
  });
}

/**
 * Inserts a column to the right of the current column
 */
export function insertColumnRight(editor: LexicalEditor): void {
  editor.update(() => {
    const ctx = getTableContextFromSelection();
    if (!ctx) return;

    const { table, colIndex } = ctx;
    const rows = table.getChildren();

    rows.forEach((r) => {
      if (!$isTableRowNode(r)) return;

      const cells = r.getChildren();
      const refCell = cells[colIndex];

      if (refCell && $isTableCellNode(refCell)) {
        // Preserve header state from the reference cell
        const headerState = refCell.getHeaderStyles();
        const newCell = $createTableCellNode(headerState);
        // Insert after the reference cell at colIndex
        refCell.insertAfter(newCell);
      } else {
        // If no reference cell, append at the end
        const newCell = $createTableCellNode(TableCellHeaderStates.NO_STATUS);
        r.append(newCell);
      }
    });
  });
}

/**
 * Internal helper to delete the current table.
 * Must be called from within an editor.update() callback.
 */
function $deleteCurrentTable(ctx?: TableContext): void {
  const context = ctx ?? getTableContextFromSelection();
  if (!context) return;

  const { table } = context;
  const p = $createParagraphNode();
  table.insertAfter(p);
  table.remove();
  p.select();
}

/**
 * Deletes the current row
 */
export function deleteRow(editor: LexicalEditor): void {
  editor.update(() => {
    const ctx = getTableContextFromSelection();
    if (!ctx) return;

    const { table, row } = ctx;
    const rowCount = table.getChildrenSize();

    if (rowCount <= 1) {
      // If this is the last row, delete the entire table within the same update
      $deleteCurrentTable(ctx);
      return;
    }

    row.remove();
  });
}

/**
 * Deletes the current column
 */
export function deleteColumn(editor: LexicalEditor): void {
  editor.update(() => {
    const ctx = getTableContextFromSelection();
    if (!ctx) return;

    const { table, colIndex } = ctx;
    const rows = table.getChildren();

    // Check if this is the last column
    const firstRow = rows[0];
    if (
      firstRow &&
      $isTableRowNode(firstRow) &&
      firstRow.getChildrenSize() <= 1
    ) {
      // If this is the last column, delete the entire table within the same update
      $deleteCurrentTable(ctx);
      return;
    }

    rows.forEach((r) => {
      if (!$isTableRowNode(r)) return;

      const cells = r.getChildren();
      const cellToRemove = cells[colIndex];

      if (cellToRemove && $isTableCellNode(cellToRemove)) {
        cellToRemove.remove();
      }
    });
  });
}

/**
 * Toggles header row state for the first row
 */
export function toggleHeaderRow(editor: LexicalEditor): void {
  editor.update(() => {
    const ctx = getTableContextFromSelection();
    if (!ctx) return;

    const { table } = ctx;
    const firstRow = table.getFirstChild();

    if (!firstRow || !$isTableRowNode(firstRow)) return;

    const firstCell = firstRow.getFirstChild();
    const isHeader =
      firstCell !== null &&
      $isTableCellNode(firstCell) &&
      firstCell.getHeaderStyles() === TableCellHeaderStates.ROW;

    firstRow.getChildren().forEach((cell) => {
      if ($isTableCellNode(cell)) {
        const newState = isHeader
          ? TableCellHeaderStates.NO_STATUS
          : TableCellHeaderStates.ROW;
        cell.setHeaderStyles(newState);
      }
    });
  });
}

/**
 * Deletes the entire table
 */
export function deleteTable(editor: LexicalEditor): void {
  editor.update(() => {
    $deleteCurrentTable();
  });
}

/**
 * Checks if the current selection is inside a table
 */
export function isSelectionInTable(): boolean {
  const selection = $getSelection();

  if ($isTableSelection(selection)) {
    return true;
  }

  if (!$isRangeSelection(selection)) {
    return false;
  }

  const cell = $findMatchingParent(
    selection.anchor.getNode(),
    $isTableCellNode
  );

  return cell !== null;
}

/**
 * Checks if the table has a header row
 */
export function hasHeaderRow(): boolean {
  const ctx = getTableContextFromSelection();
  if (!ctx) return false;

  const { table } = ctx;
  const firstRow = table.getFirstChild();

  if (!firstRow || !$isTableRowNode(firstRow)) return false;

  const firstCell = firstRow.getFirstChild();
  return (
    firstCell !== null &&
    $isTableCellNode(firstCell) &&
    firstCell.getHeaderStyles() === TableCellHeaderStates.ROW
  );
}
